import type { ContractResult, RunResult } from "@frontier/content/contracts";
import { CUSTOMERS } from "@frontier/content/progression";
import type { ContractOffer } from "@frontier/content/contract-orders";
import { contractAccessTerms } from "@frontier/content/reward-rules";
import { credits, moduleLabels } from "./ui-labels";
import { outcomeLabels, passiveInfo } from "./flight-labels";
import { equipmentDefinition } from "@frontier/content/arsenal";
import "./progression.css";
import "./run-debrief.css";

export function ContractDebrief({ result, offers = [] }: {
  result: ContractResult;
  offers?: readonly ContractOffer[];
}) {
  const award = result.reputationAward;
  const advancedUnlocked = award && offers.some((offer) => {
    const access = contractAccessTerms(offer.advanced, "advanced");
    if (!access || access.customerId !== award.customerId) return false;
    return award.balance >= access.minimumReputation &&
      award.balance - award.amount < access.minimumReputation;
  });
  return (
    <section
      className={`contract-debrief ${result.outcome}`}
      aria-label="Итог контракта"
    >
      <div className="contract-result-summary">
        <span className="contract-result-title">
          {result.difficulty === "advanced" ? "ПРОДВИНУТЫЙ / " : ""}
          {result.terms.title}
        </span>
        <b className="contract-result-status">{outcomeLabels[result.outcome]}</b>
      </div>
      <div className="debrief-payout">
        <small>НАГРАДА КОНТРАКТА</small>
        <b>+{credits(result.credits)} КР</b>
        <span>+{result.components} компонентов</span>
      </div>
      {award && (
        <div className="debrief-reputation">
          <span>{CUSTOMERS[award.customerId].name}</span>
          <b>
            +{award.amount} РЕПУТАЦИИ · {award.balance}
          </b>
          {advancedUnlocked && <small>ОТКРЫТЫ ПРОДВИНУТЫЕ КОНТРАКТЫ</small>}
        </div>
      )}
      {result.moduleUnlock && (
        <div className="debrief-unlock">
          <span>ОТКРЫТ АКТИВНЫЙ МОДУЛЬ</span>
          <b>{moduleLabels[result.moduleUnlock]}</b>
        </div>
      )}
      {result.equipmentUnlock !== "none" && (
        <div className="debrief-unlock">
          <span>ОТКРЫТО ОСНАЩЕНИЕ</span>
          <b>{passiveInfo(result.equipmentUnlock).name}</b>
        </div>
      )}
      {result.blueprintUnlocks.length > 0 && (
        <details className="debrief-blueprints">
          <summary>Открыта покупка: {result.blueprintUnlocks.length}</summary>
          <p>
            {result.blueprintUnlocks
              .map((key) => equipmentDefinition(key).name)
              .join(" · ")}
          </p>
        </details>
      )}
    </section>
  );
}

export function RunDebrief({ result, offers = [] }: {
  result: RunResult;
  offers?: readonly ContractOffer[];
}) {
  const contractCredits = result.contracts.reduce((sum, contract) => sum + BigInt(contract.credits), 0n);
  const contractComponents = result.contracts.reduce((sum, contract) => sum + contract.components, 0);
  return (
    <section className="run-debrief run-summary" aria-label="Итоги вылета">
      <div className="run-total">
        <span className="run-total-label">ПОЛУЧЕНО ЗА ВЫЛЕТ</span>
        <div className="debrief-payout">
          <b>+{credits(result.credits)} КР</b>
          <span>+{result.components} компонентов</span>
        </div>
        <small className="run-cargo-payout">
          Сдача груза: +{credits(result.cargoCredits)} КР · +
          {result.cargoComponents} комп.
        </small>
        {result.contracts.length > 0 && <small className="run-cargo-payout">Контракты: +{credits(contractCredits.toString())} КР · +{contractComponents} комп.</small>}
        {result.factionLootBonus && <small className="run-cargo-payout">Включая бонус фракции: +{credits(result.factionLootBonus.credits)} КР · +{result.factionLootBonus.components} комп.</small>}
      </div>
      <div className="run-contract-results">
        {result.contracts.map((item) => (
          <ContractDebrief key={item.orderId} result={item} offers={offers} />
        ))}
      </div>
    </section>
  );
}
